import { apiService } from "./api.service";

async function get<T = any>(path: string): Promise<T> {
  const res = await apiService.fetchWithAuth(path);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Error en la petición");
  return json.data as T;
}

async function post<T = any>(path: string, body?: any): Promise<T> {
  const res = await apiService.fetchWithAuth(path, {
    method: "POST",
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Error en la petición");
  return json.data as T;
}

export interface DashboardSummary {
  activeProjects: number;
  averageProgress: number;
  tokenBalance: number;
  pendingDocuments: number;
}

export interface RecentProject {
  id: string;
  name: string;
  status: string;
  progress: number;
  budget: number;
  address: string | null;
  startDate: number | null;
  endDate: number | null;
  updatedAt: number;
  lastAdvanceAt: number | null;
}

export interface RecentAdvance {
  id: string;
  projectId: string;
  projectName: string;
  weekNumber: number;
  year: number;
  progressPercentage: number;
  status: string;
  description: string | null;
  createdAt: number;
}

export interface ClientDashboard {
  summary: DashboardSummary;
  recentProjects: RecentProject[];
  recentAdvances: RecentAdvance[];
  unreadMessages: number;
  pendingChangeOrders: number;
}

export interface ProjectItem {
  id: string;
  name: string;
  description: string | null;
  status: string;
  progress: number;
  budget: number;
  address: string | null;
  startDate: number | null;
  endDate: number | null;
  coverImageUrl: string | null;
  projectType: string;
  projectLeadName: string | null;
  projectLeadEmail: string | null;
  lastAdvanceAt: number | null;
  documentsCount: number;
  advancesCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface ProjectAdvanceItem {
  id: string;
  weekNumber: number;
  year: number;
  progressPercentage: number;
  status: string;
  description: string | null;
  contractorSpecialty: string | null;
  createdAt: number;
}

export interface ProjectDocumentItem {
  id: string;
  name: string;
  type: string;
  category: string | null;
  size: number;
  signed: boolean;
  signedAt: number | null;
  uploadedByName: string | null;
  createdAt: number;
}

export interface ProjectChangeItem {
  id: string;
  description: string;
  costImpact: number;
  status: string;
  approvedAt: number | null;
  createdAt: number;
}

export interface ProjectFinancials {
  totalInvoiced: number;
  totalPaid: number;
  pendingAmount: number;
  pendingInvoices: number;
}

export interface ProjectDetail {
  project: ProjectItem;
  financials: ProjectFinancials;
  recentAdvances: ProjectAdvanceItem[];
  recentDocuments: ProjectDocumentItem[];
  recentChanges: ProjectChangeItem[];
}

export interface AdvanceItem {
  id: string;
  projectId: string;
  projectName: string;
  weekNumber: number;
  year: number;
  progressPercentage: number;
  status: string;
  description: string | null;
  contractorSpecialty: string | null;
  createdAt: number;
}

export interface AdvancePhoto {
  id: string;
  url: string;
  thumbnailUrl: string | null;
  caption: string | null;
  order: number;
  uploadedAt: number;
}

export interface AdvanceMaterial {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  costPerUnit: number;
  totalCost: number;
  notes: string | null;
}

export interface AdvanceDetail {
  advance: {
    id: string;
    projectId: string;
    projectName: string;
    contractorId: string;
    contractorName: string | null;
    contractorSpecialty: string | null;
    weekNumber: number;
    year: number;
    weekStart: number;
    weekEnd: number;
    description: string | null;
    progressPercentage: number;
    status: string;
    approvedByName: string | null;
    approvedAt: number | null;
    rejectedReason: string | null;
    createdAt: number;
    updatedAt: number;
  };
  photos: AdvancePhoto[];
  materials: AdvanceMaterial[];
}



export interface DocumentItem {
  id: string;
  projectId: string;
  projectName: string;
  name: string;
  type: string;
  category: string | null;
  url: string;
  size: number;
  signed: boolean;
  signedAt: number | null;
  uploadedByName: string | null;
  createdAt: number;
}

export interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
  taxRate: number;
  taxTotal: number;
  discount: number;
}

export interface ConversationItem {
  projectId: string;
  projectName: string;
  projectStatus: string;
  projectLeadName: string | null;
  lastMessagePreview: string | null;
  lastMessageAt: number | null;
  unreadCount: number;
}

export interface MessageItem {
  id: string;
  projectId: string;
  content: string;
  type: string;
  isFromClient: boolean;
  senderName: string;
  readAt: number | null;
  createdAt: number;
  attachments: MessageAttachment[];
}

export interface MessageAttachment {
  url: string;
  name: string;
  type: string;
  size: number;
}

export interface ProjectLeadInfo {
  id: string | null;
  name: string | null;
  email: string | null;
  avatarUrl: string | null;
}

export interface MessagesResponse {
  messages: MessageItem[];
  projectLead: ProjectLeadInfo | null;
}

export interface TokenTransactionItem {
  id: string;
  amount: number;
  type: string;
  description: string;
  referenceId: string | null;
  status: string;
  createdAt: number;
}

export interface TokenPackage {
  id: string;
  name: string;
  tokens: number;
  price: number;
  description: string | null;
  popular: boolean;
  savings: number;
}

export interface PromoValidationResult {
  valid: boolean;
  code?: string;
  bonusTokens?: number;
  description?: string | null;
  reason?: string;
}



export interface ChangeOrderListItem {
  id: string;
  projectId: string;
  projectName: string;
  number: string | null;
  title: string | null;
  description: string;
  category: string;
  priority: string;
  status: string;
  costImpact: number;
  requestedByName: string | null;
  clientApprovedAt: number | null;
  clientRejectedAt: number | null;
  approvedAt: number | null;
  executedAt: number | null;
  rejectionReason: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface ChangeOrderAttachment {
  url: string;
  name: string;
  type: string;
  size: number;
}

export interface ChangeOrderItem {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
  category: string;
  type: 'addition' | 'deduction' | 'modification';
  notes?: string;
}

export interface ChangeOrderImpact {
  originalBudget?: number;
  newBudget?: number;
  impact?: number;
  impactPercentage?: number;
  itemsSummary?: {
    additions: number;
    deductions: number;
    modifications: number;
    netChange: number;
  };
  categoryBreakdown?: Record<string, number>;
  recommendation?: string;
  riskLevel?: string;
  timelineImpact?: number;
}

export interface ChangeOrderDetail {
  id: string;
  projectId: string;
  projectName: string;
  number: string | null;
  title: string | null;
  description: string;
  category: string;
  priority: string;
  status: string;
  costImpact: number;
  items: ChangeOrderItem[];
  impact: ChangeOrderImpact;
  attachments: ChangeOrderAttachment[];
  metadata: Record<string, any>;
  requestedByName: string | null;
  approvedByName: string | null;
  approvedAt: number | null;
  executedAt: number | null;
  clientApprovedAt: number | null;
  clientRejectedAt: number | null;
  clientSignatureName: string | null;
  clientSignatureSvg: string | null;
  rejectionReason: string | null;
  createdAt: number;
  updatedAt: number;
}



export interface FinancialSummary {
  totalInvoiced: number;
  totalPaid: number;
  pendingAmount: number;
  overdueAmount: number;
  totalInvoices: number;
  pendingInvoicesCount: number;
}

export interface InvoiceListItem {
  id: string;
  number: string;
  projectId: string | null;
  projectName: string | null;
  dateIssued: number;
  dateDue: number;
  datePaid: number | null;
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  paidAmount: number;
  pendingAmount: number;
  status: string;
  notes: string | null;
  pendingReceipts: number;
}


export interface InvoicePayment {
  id: string;
  amount: number;
  method: string;
  reference: string | null;
  datePaid: number;
  notes: string | null;
  receiptPhotoUrl: string | null;
  source: string;
  createdAt: number;
}

export interface PaymentReceipt {
  id: string;
  fileUrl: string;
  fileSize: number;
  fileType: string | null;
  comment: string | null;
  amountDeclared: number | null;
  methodDeclared: string | null;
  referenceDeclared: string | null;
  status: "pending" | "approved" | "rejected";
  rejectionReason: string | null;
  createdAt: number;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  projectId?: string | null;
  projectName?: string | null;
}

export interface InvoiceDetail {
  invoice: {
    id: string;
    number: string;
    projectId: string | null;
    projectName: string | null;
    dateIssued: number;
    dateDue: number;
    datePaid: number | null;
    subtotal: number;
    tax: number;
    discount: number;
    total: number;
    paidAmount: number;
    pendingAmount: number;
    status: string;
    notes: string | null;
    paymentMethod: string | null;
    paymentReference: string | null;
  };
  items: InvoiceItem[];
  payments: InvoicePayment[];
  receipts: PaymentReceipt[];
}

export interface DocumentListItem {
  id: string;
  projectId: string;
  projectName: string;
  name: string;
  type: string;
  category: string;
  size: number;
  signed: boolean;
  signedAt: number | null;
  requiresSignature: boolean;
  uploadedByMe: boolean;
  uploadedByName: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface DocumentDetail {
  id: string;
  projectId: string;
  projectName: string;
  name: string;
  type: string;
  category: string;
  url: string;
  size: number;
  signed: boolean;
  signedAt: number | null;
  signedByName: string | null;
  signedIp: string | null;
  signedUserAgent: string | null;
  signatureImageUrl: string | null;
  requiresSignature: boolean;
  uploadedByMe: boolean;
  uploadedByName: string | null;
  createdAt: number;
  updatedAt: number;
  metadata: Record<string, any>;
}


export const clientApiService = {
  listChangeOrders: (projectId?: string) => {
    const q = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
    return get<ChangeOrderListItem[]>(`/api/client/change-orders${q}`);
  },
  getChangeOrder: (id: string) =>
    get<ChangeOrderDetail>(`/api/client/change-orders/${id}`),
  approveChangeOrder: (id: string, data: { signatureSvg: string; signatureName: string }) =>
    post<ChangeOrderDetail>(`/api/client/change-orders/${id}/approve`, data),
  rejectChangeOrder: (id: string, reason: string) =>
    post<ChangeOrderDetail>(`/api/client/change-orders/${id}/reject`, { reason }),
  
  
  listTokenPackages: () => get<TokenPackage[]>("/api/client/tokens/packages"),

  getTokenBalance: () => get<{ balance: number }>("/api/client/tokens/balance"),

  listTokenTransactions: (limit = 50) =>
    get<TokenTransactionItem[]>(`/api/client/tokens/transactions?limit=${limit}`),

  validatePromo: (code: string) =>
    post<PromoValidationResult>("/api/client/tokens/validate-promo", { code }),

  createTokenCheckout: (data: { packageId: string; promoCode?: string }) =>
    post<{ url: string; sessionId: string }>("/api/client/tokens/checkout", data),

  listConversations: () => get<ConversationItem[]>("/api/client/messages/conversations"),

  listMessages: (projectId: string) =>
    get<MessagesResponse>(`/api/client/messages/${projectId}`),

  sendMessage: (data: { projectId: string; content: string; attachments?: MessageAttachment[] }) =>
    post<MessageItem>("/api/client/messages", data),

  markMessagesRead: (projectId: string) =>
    post(`/api/client/messages/${projectId}/read`),

  presignMessageUpload: (data: { filename: string; contentType: string }) =>
    post<{ upload_url: string; public_url: string; key: string; expires_at: number }>(
      "/api/client/messages/presign-upload",
      data
    ),
  listDocuments: (opts?: { projectId?: string; type?: string; status?: string }) => {
    const params = new URLSearchParams();
    if (opts?.projectId) params.set("projectId", opts.projectId);
    if (opts?.type && opts.type !== "all") params.set("type", opts.type);
    if (opts?.status && opts.status !== "all") params.set("status", opts.status);
    const q = params.toString();
    return get<DocumentListItem[]>(`/api/client/documents/list${q ? `?${q}` : ""}`);
  },

  getDocumentDetail: (id: string) =>
    get<DocumentDetail>(`/api/client/documents/detail/${id}`),

  presignDocumentUpload: (data: { filename: string; contentType: string; projectId: string }) =>
    post<{ upload_url: string; public_url: string; key: string; expires_at: number }>(
      "/api/client/documents/presign-upload",
      data
    ),

  createDocument: (data: {
    projectId: string;
    name: string;
    category?: string;
    url: string;
    size: number;
  }) => post<DocumentDetail>("/api/client/documents", data),

  signDocument: (id: string, data: { signatureSvg: string; signedName: string }) =>
    post<DocumentDetail>(`/api/client/documents/${id}/sign`, data),
  getAdvance: (id: string) => get<AdvanceDetail>(`/api/client/advances/${id}`),
  getFinancialSummary: (projectId?: string) =>
    get<FinancialSummary>(
      `/api/client/finance/summary${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`
    ),
  listInvoices: (opts?: { projectId?: string; status?: string }) => {
    const params = new URLSearchParams();
    if (opts?.projectId) params.set("projectId", opts.projectId);
    if (opts?.status && opts.status !== "all") params.set("status", opts.status);
    const q = params.toString();
    return get<InvoiceListItem[]>(`/api/client/invoices${q ? `?${q}` : ""}`);
  },
  getInvoice: (id: string) => get<InvoiceDetail>(`/api/client/invoices/${id}`),
  listReceipts: () => get<PaymentReceipt[]>("/api/client/receipts"),

  presignReceiptUpload: (data: { filename: string; contentType: string; invoiceId?: string }) =>
    post<{ upload_url: string; public_url: string; key: string; expires_at: number }>(
      "/api/client/receipts/presign-upload",
      data
    ),

  createReceipt: (data: {
    invoiceId?: string;
    projectId?: string;
    fileUrl: string;
    fileSize: number;
    fileType: string;
    comment?: string;
    amountDeclared?: number;
    methodDeclared?: string;
    referenceDeclared?: string;
  }) => post<{ id: string; createdAt: number }>("/api/client/receipts", data),
  getDashboard: () => get<ClientDashboard>("/api/client/dashboard"),
  listProjects: () => get<ProjectItem[]>("/api/client/projects"),
  getProject: (id: string) => get<ProjectDetail>(`/api/client/projects/${id}`),
  listAdvances: (projectId?: string) =>
    get<AdvanceItem[]>(
      `/api/client/advances${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`
    )
};